import { Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { AuditDto } from "@audit-plan/shared";
import { usePeople } from "../../api/people";
import { AuditStatusPill, OutcomePill, Rating, fmtN, fmtWhen } from "../pills";
import { ResponsiveList } from "../ResponsiveList";

/**
 * NC aperte dell'audit (AUD-F8.1): il numero, 0 se sono tutte chiuse. «—» prima della fine dell'esecuzione (le NC nascono
 * al completamento) e sugli audit storici senza NC nel portale: le NC dello storico arrivano dai registri (AUD-F8.2).
 */
export function OpenNcCell({ a }: { a: Pick<AuditDto, "status" | "historical" | "ncCount" | "openNcCount"> }) {
  const { t } = useTranslation();
  if (a.ncCount == null || a.status === "planned" || a.status === "confirmed" || a.status === "in_progress") return <>—</>;
  if (a.historical && !a.ncCount) return <Tooltip title={t("nc_histMissing")}><span style={{ opacity: 0.6 }}>—</span></Tooltip>;
  const n = a.openNcCount ?? 0;
  return n ? <Tag color="error" style={{ marginInlineEnd: 0 }}>{n}</Tag> : <span className="num">0</span>;
}

/** Origine dell'audit: "Da programma" o "Cliente: X". */
export function OriginLabel({ a }: { a: Pick<AuditDto, "origin" | "client"> }) {
  const { t } = useTranslation();
  return a.origin === "client" ? <Tag color="gold" style={{ marginInlineEnd: 0 }}>{t("c_client")}: {a.client}</Tag> : <span style={{ opacity: 0.7 }}>{t("c_origin_program")}</span>;
}

/** Stabilimento auditato: nome, codice SAP in monospazio. */
export function FactoryLabel({ a }: { a: Pick<AuditDto, "factoryName" | "factoryCode" | "supplierName"> }) {
  return (
    <span>
      {a.factoryName}
      {a.factoryCode ? <span style={{ fontFamily: "var(--ap-mono)", opacity: 0.6 }}> {a.factoryCode}</span> : null}
    </span>
  );
}

export function AuditTable(props: { audits: AuditDto[]; withSupplier?: boolean; withFactory?: boolean; loading?: boolean; pageSizeKey?: string }) {
  const { audits, withSupplier, loading } = props;
  // con il fornitore si mostra anche lo stabilimento, se diverso dalla sede omonima
  const withFactory = props.withFactory ?? (withSupplier && audits.some((a) => a.factoryName !== a.supplierName));
  const { t } = useTranslation();
  const nav = useNavigate();
  const people = usePeople();
  const cols: ColumnsType<AuditDto> = [
    { title: t("code"), dataIndex: "code", render: (c: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{c}</span> },
    ...(withSupplier ? [{ title: t("supplier"), dataIndex: "supplierName" } as ColumnsType<AuditDto>[number]] : []),
    ...(withFactory ? [{ title: t("f_factory"), dataIndex: "factoryName", render: (_: unknown, a: AuditDto) => <FactoryLabel a={a} /> } as ColumnsType<AuditDto>[number]] : []),
    { title: t("auditType"), dataIndex: "type", render: (x: string) => t(`t_${x}`) },
    { title: t("c_origin"), dataIndex: "origin", render: (_: unknown, a) => <OriginLabel a={a} /> },
    { title: t("plannedDate"), dataIndex: "plannedDate", render: (d: string | null, a: AuditDto) => (d ? fmtWhen(a) : <span style={{ opacity: 0.6 }}>{t("dateHidden")}</span>) },
    { title: t("auditor"), dataIndex: "auditorId", render: (id: string) => people.name(id) },
    { title: t("status"), render: (_: unknown, a) => <AuditStatusPill a={a} /> },
    {
      title: t("score"),
      align: "right",
      render: (_: unknown, a) => (a.scores?.total != null ? <span><Rating score={a.scores.total} override={a.ratingOverride} reason={a.ratingReason} /> <span className="num">{fmtN(a.scores.total, 1)}</span></span> : a.outcome === "pass" || a.outcome === "fail" ? <OutcomePill outcome={a.outcome} /> : "—"),
    },
    { title: t("openNc"), dataIndex: "openNcCount", align: "right", render: (_: unknown, a) => <OpenNcCell a={a} /> },
  ];
  return (
    <ResponsiveList<AuditDto>
      rowKey="id"
      loading={loading}
      dataSource={audits}
      columns={cols}
      pageSize={props.pageSizeKey ? 10 : 20}
      pageSizeKey={props.pageSizeKey}
      onRowClick={(a) => nav(`/audits/${a.id}`)}
      emptyText={t("noResults")}
      card={{
        code: (a) => a.code,
        title: (a) => (withSupplier ? a.supplierName : t(`t_${a.type}`)),
        subtitle: (a) => (withSupplier ? t(`t_${a.type}`) : null),
        tags: (a) => (
          <>
            <AuditStatusPill a={a} />
            {a.scores?.total != null && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Rating score={a.scores.total} override={a.ratingOverride} reason={a.ratingReason} /> <span className="num">{fmtN(a.scores.total, 1)}</span>
              </span>
            )}
            <OutcomePill outcome={a.outcome} />
            {a.openNcCount ? <Tag color="error">{a.openNcCount} NC</Tag> : null}
          </>
        ),
        fields: (a) => [
          ...(withFactory ? [[t("f_factory"), <FactoryLabel key="f" a={a} />] as [string, React.ReactNode]] : []),
          [t("plannedDate"), a.plannedDate ? fmtWhen(a) : <span style={{ opacity: 0.6 }}>{t("dateHidden")}</span>],
          [t("auditor"), people.name(a.auditorId)],
          [t("c_origin"), <OriginLabel key="o" a={a} />],
        ],
      }}
    />
  );
}
